'use client';
import { ModelWithChildren, ToneAndChildren, roundFloat, type EsrEntry, type Training } from '@/utils/helpers';
import {
  AudioLines,
  ChevronDown,
  Download,
  Gauge,
  RefreshCw,
  AlertTriangle,
  GripVertical,
  ChevronUp,
} from 'lucide-react';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { TRAIN_TYPE, FORMAT, ARCHITECTURE_VERSION, type ArchitectureVersion } from '@/utils/constants';
import { esrSizeLabel, preferredArchitecture, sortedEsrs } from '@/utils/architecture';
import { createClient } from '@/utils/supabase/client';
import { getToneByIdRpc } from '@/utils/supabase/queries';
import { LogModal } from '../LogModal/LogModal';
import { FirstA2DownloadModal, shouldShowFirstA2Modal } from './FirstA2DownloadModal';
import { usePluginDownloadIntercept } from '../PluginDownloadModal/PluginDownloadModal';
import { LogoSm } from '@/components/icons/Logo';
import Status from '../Tones/Status/Status';
import { useTrainingLive } from '../Tones/Status/TrainingsProvider';
import CircleLoading from '../CircleLoading/CircleLoading';
import LoadingDots from '../LoadingDots';
import { ModelMenu } from './ModelMenu';
import { Button } from '../Button/Button';
import { DownloadAllButton } from '../Tones/Actions';
import ArchitectureSegmentedControl from './ArchitectureSegmentedControl';
import { analytics } from '@/lib/analytics';
import Link from 'next/link';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { modelFileUrl, pageShareToken } from '@/utils/models/file-url';

const EM_DASH = '—';

const capitalize = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const archesPresentIn = (ms: ModelWithChildren[]): Set<ArchitectureVersion> => {
  const s = new Set<ArchitectureVersion>();
  for (const m of ms) {
    if (m.architecture_version) s.add(m.architecture_version as ArchitectureVersion);
  }
  return s;
};

const architecturePrefix = (arch: string | null | undefined): string => {
  if (arch === ARCHITECTURE_VERSION.A2) return 'A2';
  if (arch === ARCHITECTURE_VERSION.A1) return 'A1';
  if (arch === ARCHITECTURE_VERSION.CUSTOM) return 'Custom';
  return '';
};

function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
}

interface ExpandCollapseProps {
  title: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
  isTraining?: boolean;
  isError?: boolean;
  /**
   * When true, render the header as a non-interactive label (no toggle, no
   * chevron). Used for the empty-state container when the selected
   * architecture has no models.
   */
  disabled?: boolean;
}

const Expand = ({
  title,
  children,
  defaultExpanded = false,
  isTraining,
  isError,
  disabled = false,
}: ExpandCollapseProps) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const expanded = !disabled && isExpanded;

  return (
    <div
      className={`rounded-xl border ${
        disabled
          ? 'border-zinc-700 opacity-60'
          : expanded
            ? 'border-white'
            : 'border-zinc-700'
      }`}
    >
      {disabled ? (
        <div className="w-full px-4 py-3 lg:py-4 lg:px-8 flex items-start justify-between font-semibold">
          <div className="flex flex-row gap-4 items-center">
            <span className="font-bold text-base text-left">{title}</span>
          </div>
        </div>
      ) : (
        <button
          className="w-full px-4 py-3 lg:py-4 lg:px-8 flex items-start justify-between font-semibold focus:outline-none"
          onClick={() => setIsExpanded(!isExpanded)}
          aria-label={`${title} ${expanded ? 'Collapse' : 'Expand'}`}
        >
          <div className="flex flex-row gap-4 items-center">
            <span className="font-bold text-base text-left">{title}</span>
            {isTraining && (
              <CircleLoading aria-label="Training" strokeWidth={6} size={24} color={'#d4d4d8'} />
            )}
            {isError && <AlertTriangle aria-label="Error" size={24} color={'#FF0000'} />}
          </div>

          {expanded ? (
            <ChevronUp aria-label="Collapse" size={24} />
          ) : (
            <ChevronDown aria-label="Expand" size={24} />
          )}
        </button>
      )}
      <div
        className={`px-0 py-0 lg:py-2 border-t border-white w-full ${expanded ? 'block' : 'hidden'}`}
      >
        {children}
      </div>
    </div>
  );
};

interface MetricPillProps {
  arch: string | null | undefined;
  model: ModelWithChildren;
  training: Partial<Training> | null;
  /**
   * Optional pulse class to layer on each pill. Used to indicate that
   * the ESR values are still being polled and will change. Pulse is
   * applied per-pill (not on a wrapping element) so the parent row's
   * non-dynamic siblings (e.g. the train-type pill) stay static.
   */
  pulseClassName?: string;
}

const PILL_BASE_CLASS =
  'bg-zinc-900 rounded-md px-2 py-1 text-zinc-400 flex flex-row gap-2 items-center whitespace-nowrap';

const MetricPills = ({ arch, model, training, pulseClassName }: MetricPillProps) => {
  const archLabel = architecturePrefix(arch);
  const esr = training?.esr ? roundFloat(training.esr, 4) : EM_DASH;
  const size = model.size ? capitalize(model.size) : null;
  const pillClass = pulseClassName ? `${PILL_BASE_CLASS} ${pulseClassName}` : PILL_BASE_CLASS;

  // A2: one pill per esrs[] entry (size-bucketed). When the training row has
  // no esrs entries, fall back to a single arch+size pill — but only if we
  // can resolve a real size. "A2-— ESR: —" is garbage; suppress instead.
  if (arch === ARCHITECTURE_VERSION.A2) {
    const esrs = (training?.esrs ?? null) as EsrEntry[] | null;
    if (esrs && esrs.length > 0) {
      return (
        <>
          {sortedEsrs(esrs).map((entry) => (
            <p key={entry.max_value} className={pillClass}>
              <span className="text-white">{`${archLabel}-${esrSizeLabel(entry.max_value)}`}</span>
              <span>{`ESR: ${roundFloat(entry.esr, 4)}`}</span>
            </p>
          ))}
        </>
      );
    }
    if (!size) return null;
    return (
      <p className={pillClass}>
        <span className="text-white">{`${archLabel}-${size}`}</span>
        <span>{`ESR: ${esr}`}</span>
      </p>
    );
  }
  // Custom: just the arch label, no size — size is redundant for Custom and
  // will be removed from the schema. Always renders since "Custom" is itself
  // meaningful data; ESR may be em-dash.
  if (arch === ARCHITECTURE_VERSION.CUSTOM) {
    return (
      <p className={pillClass}>
        <span className="text-white">{archLabel}</span>
        <span>{`ESR: ${esr}`}</span>
      </p>
    );
  }
  // A1: arch-prefixed pill including size (e.g. "A1-Full ESR: 0.0001"). Only
  // render when we can resolve a real size — "A1-—" is garbage. Omit the ESR
  // span when there's no real value rather than showing "ESR: —".
  if (arch === ARCHITECTURE_VERSION.A1) {
    if (!size) return null;
    const hasEsr = esr !== EM_DASH;
    return (
      <p className={pillClass}>
        <span className="text-white">{`${archLabel}-${size}`}</span>
        {hasEsr && <span>{`ESR: ${esr}`}</span>}
      </p>
    );
  }
  // No architecture (non-NAM platforms with trainings, e.g. aida-x): plain
  // ESR pill with optional size prefix.
  return (
    <p className={pillClass}>
      {size && <span className="text-white">{size}</span>}
      <span>{`ESR: ${esr}`}</span>
    </p>
  );
};

const Model = ({
  model,
  toneId,
  userId,
  platform,
  isApiSelect = false,
  isOAuthFlow = false,
  dragHandle = null,
  onA2Downloaded,
  interceptDownload,
}: {
  model: ModelWithChildren;
  toneId: number;
  userId: string;
  platform: FORMAT;
  isApiSelect?: boolean;
  isOAuthFlow?: boolean;
  dragHandle?: React.ReactNode;
  onA2Downloaded?: () => void;
  interceptDownload?: (action: () => void) => void;
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const isOwner = model.user_id === userId;

  const downloadModel = async () => {
    analytics.capture('model.downloaded');
    if (model.architecture_version === ARCHITECTURE_VERSION.A2) {
      onA2Downloaded?.();
    }
    setIsDownloading(true);
    try {
      // Download model
      if (!model.model_url) return null;

      const response = await fetch(modelFileUrl({ id: model.id, model_url: model.model_url }, { token: pageShareToken() }));
      if (!response.ok) {
        console.error('Failed to download model:', model.name);
        return null;
      }

      const data = await response.arrayBuffer(); // or response.text() if it's JSON text
      const blob = new Blob([data], { type: 'application/octet-stream' });
      // Extract extension from model_url or default to .nam
      const extension = model.model_url.split('.').pop() || 'nam';
      const fileName = `${model.name || 'model'}.${extension}`;

      if (isIOS()) {
        // Generate and download zip file
        const reader = new FileReader();
        reader.onload = function () {
          const a = document.createElement('a');
          a.href = reader.result as string;
          a.download = fileName;
          a.click();
        };
        reader.readAsDataURL(blob);
      } else {
        // Generate and download zip file
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Failed to download model:', error);
      alert(`Failed to download model: ${error}`);
    }
    const data = {
      tone_id: toneId,
    } as { tone_id: number | null; user_id?: string };
    if (userId) {
      data.user_id = userId;
    }
    try {
      const response = await fetch('/api/downloads', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      // ✅ IMPORTANT: Consume the response body
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Failed to record download:', errorData);
      } else {
        // Even if you don't need the data, consume the body
        await response.json();
      }
    } catch (error) {
      console.error('Failed to record download:', error);
    }
    setIsDownloading(false);
  };

  // Subscribe to live training updates. `useTrainingLive` returns the
  // latest polled row when TrainingsProvider is polling (i.e. at least
  // one training on the page is active), otherwise undefined — in which
  // case we fall back to the SSR snapshot. Merge so live fields win but
  // SSR-only computed fields (e.g. has_logs after the initial render)
  // still survive between polls.
  const liveTraining = useTrainingLive(model.training?.id);
  const training: Partial<Training> | null = model.training
    ? { ...model.training, ...(liveTraining ?? {}) }
    : null;

  const isTraining = !!training && !training.is_error && !training.is_success;
  const isError = !!training && !!training.is_error;
  // Retry is offered ONLY for unknown errors (GPU crash / hang /
  // heartbeat timeout) — `is_error` set with no classified `error`.
  // Both fields are in the live polling projection, so this flips on
  // the moment the run fails without a page refresh.
  const canRetry = isError && !training?.error;
  const arch = model.architecture_version;

  // Pulse the metric values while a training is still in flight, so the
  // user sees that the numbers (epochs, ESR) are mid-flight and will
  // continue to change. Pure CSS — no JS animation loop.
  //
  // Applied per-item (ESR pills, Epochs) — NOT to the train-type pill,
  // which is set at capture time and doesn't change during training.
  const pulseClass = isTraining ? 'animate-metric-pulse' : '';

  const esrs = (training?.esrs ?? null) as EsrEntry[] | null;
  const hasEsrData = (esrs && esrs.length > 0) || (training?.esr != null && training.esr > 0);
  const hasEpochs = training?.epochs != null && training.epochs > 0;
  // Trainings hide the metrics row until BOTH ESR data (`esrs[]` or
  // legacy `esr`) AND `epochs` are populated — otherwise we render
  // partial junk like "ESR: — 0 Epochs Sweep" during preprocess. Once
  // polling lands both values the row appears.
  // Models without a related training (e.g. raw uploads) keep showing
  // the metrics row in the no-training branch below.
  const showTrainingMetricsRow = !!training && hasEsrData && hasEpochs;
  // Matches tones_aggs.has_model_with_calibration (input_level set); also
  // treat output_level alone as calibrated since either level is enough
  // for NAM level-matching.
  const isCalibrated = model.input_level != null || model.output_level != null;

  // `has_logs` is a Postgres computed column (see
  // 20260522123000_trainings_has_logs_fn.sql) — polled by TrainingsProvider
  // via `defaultTrainingFields`. Falls back to the SSR value before the
  // first poll lands.
  const hasLogs = !!(training as (Training & { has_logs?: boolean }) | null)?.has_logs;

  const calibrationLabel = isCalibrated ? (
    <p className="text-zinc-400 flex flex-row gap-2 items-center whitespace-nowrap">
      <Gauge size={16} className="flex-shrink-0" aria-hidden />
      cal.
    </p>
  ) : null;

  return (
    <div className="flex flex-col gap-6 w-full min-w-0">
      <div className="flex flex-row gap-2 items-center">
        {dragHandle}
        {!isApiSelect && (
          <button
            onClick={() =>
              interceptDownload ? interceptDownload(downloadModel) : downloadModel()
            }
            className={`flex flex-1 text-left items-center gap-2 min-w-0 justify-between w-full ${
              !model.model_url ? 'cursor-not-allowed' : ''
            }`}
            disabled={!model.model_url || isDownloading}
          >
            <div className="min-w-0 flex-1">
              <h4 className="font-semibold line-clamp-2">{model.name}</h4>
            </div>
            {isDownloading ? (
              <LoadingDots />
            ) : (
              <Download
                aria-label="Download model"
                className={`${!model.model_url ? 'text-zinc-500' : 'text-white'} w-6 h-6 flex-shrink-0`}
              />
            )}
          </button>
        )}
        {isApiSelect && (
          <div className="min-w-0 flex-1">
            <h4 className="font-semibold line-clamp-2">{model.name}</h4>
          </div>
        )}
        {!isApiSelect && isOwner && (
          <ModelMenu
            isTraining={isTraining}
            model={model}
            platform={platform}
            userId={userId}
            toneId={toneId}
            canRetry={canRetry}
            trainingId={training?.id}
          />
        )}
      </div>
      {!!training && (
        <>
          {(showTrainingMetricsRow || isCalibrated) && (
            <div className="flex flex-row gap-4 flex-wrap items-center font-mono text-xs">
              {showTrainingMetricsRow && (
                <>
                  <MetricPills
                    arch={arch}
                    model={model}
                    training={training}
                    pulseClassName={pulseClass}
                  />
                  {hasEpochs && (
                    <p
                      className={`text-zinc-400 flex flex-row gap-2 items-center whitespace-nowrap ${pulseClass}`}
                    >
                      <RefreshCw size={16} className="flex-shrink-0" />
                      {`${training.epochs} Epochs`}
                    </p>
                  )}
                  <p className="text-zinc-400 flex flex-row gap-2 items-center whitespace-nowrap">
                    <AudioLines size={16} className="flex-shrink-0" />
                    {training.type === TRAIN_TYPE.SWEEP_V3
                      ? 'Sweep'
                      : training.type === TRAIN_TYPE.CONVERT
                        ? 'Convert'
                        : training.type
                          ? 'Dry/Wet'
                          : EM_DASH}
                  </p>
                </>
              )}
              {calibrationLabel}
            </div>
          )}
          {hasLogs && training.id && (
            <LogModal
              id={training.id}
              tracking={{
                event: 'tone.training_logs_modal.opened',
              }}
            />
          )}
          {(isTraining || isError) && (
            <Status modelName={model.name!} isOwner={isOwner} training={training} />
          )}
          <Link
            href="/capture"
            target={isApiSelect ? '_blank' : undefined}
            className="flex flex-row gap-2 items-center"
          >
            <p className="text-zinc-400 text-xs">Captured on</p>
            <LogoSm width={42} height={14} />
          </Link>
        </>
      )}
      {!training &&
        ((!!model.size && arch !== ARCHITECTURE_VERSION.A2) || isCalibrated) && (
          <div className="flex flex-row gap-4 flex-wrap items-center font-mono text-xs">
            {!!model.size && arch !== ARCHITECTURE_VERSION.A2 && (
              <MetricPills arch={arch} model={model} training={null} />
            )}
            {calibrationLabel}
          </div>
        )}
    </div>
  );
};

const SortableModel = ({
  model,
  toneId,
  userId,
  platform,
  isLast,
  hidden,
  isApiSelect,
  isOAuthFlow,
  dragSessionActive,
  onA2Downloaded,
  interceptDownload,
}: {
  model: ModelWithChildren;
  toneId: number;
  userId: string;
  platform: FORMAT;
  isLast: boolean;
  hidden: boolean;
  isApiSelect: boolean;
  isOAuthFlow: boolean;
  dragSessionActive: boolean;
  onA2Downloaded?: () => void;
  interceptDownload?: (action: () => void) => void;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: model.id,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    // Avoid transition + frequent transform updates during drag/autoscroll (major scroll jank).
    transition: dragSessionActive ? undefined : transition,
    zIndex: isDragging ? 10 : 'auto',
    position: 'relative',
  };

  const handle = (
    <button
      type="button"
      {...attributes}
      {...listeners}
      aria-label="Drag to reorder"
      className="touch-none cursor-grab active:cursor-grabbing text-zinc-400 hover:text-white flex-shrink-0 py-1 -ml-1 pl-0"
    >
      <GripVertical size={20} />
    </button>
  );

  return (
    <div ref={setNodeRef} className={`w-full ${hidden ? 'hidden' : ''}`}>
      <div
        style={style}
        className={`w-full min-w-0 px-4 lg:px-8 py-6 rounded-md ${
          isDragging ? 'bg-zinc-900/60' : ''
        }`}
      >
        <Model
          model={model}
          toneId={toneId}
          userId={userId}
          platform={platform}
          isApiSelect={isApiSelect}
          isOAuthFlow={isOAuthFlow}
          dragHandle={handle}
          onA2Downloaded={onA2Downloaded}
          interceptDownload={interceptDownload}
        />
      </div>
      {!isLast && (
        <div
          className="mt-6 h-px bg-zinc-700 shrink-0 mx-4 lg:mx-8"
          role="separator"
          aria-hidden
        />
      )}
    </div>
  );
};

const Models = ({
  tone,
  models,
  toneId,
  userId,
  platform,
  isApiSelect = false,
  isOAuthFlow = false,
  canReorder = false,
  architecture,
  partnerArchitectures = null,
  isGearUnsupported = false,
  selectGears,
}: {
  tone: ToneAndChildren;
  models: ModelWithChildren[];
  toneId: number;
  userId: string;
  platform: FORMAT;
  isApiSelect?: boolean;
  isOAuthFlow?: boolean;
  canReorder?: boolean;
  /** Partner-arch override for the segmented control's initial selection (Select flow). */
  architecture?: ArchitectureVersion;
  /**
   * Architectures the OAuth partner will accept on `/load/model`. When set
   * (Select-flow + active OAuth flow), the segmented control still exposes
   * every architecture the tone has, but selecting one outside this set
   * disables the model list and Download All. `null` = no partner gating.
   */
  partnerArchitectures?: ArchitectureVersion[] | null;
  /** Forwarded to the internal Select-NAM Download All button. */
  isGearUnsupported?: boolean;
  /** Forwarded to the internal Select-NAM Download All button. */
  selectGears?: string[];
}) => {
  const t = useTranslations('models');
  const [showAll, setShowAll] = useState(false);
  const [localModels, setLocalModels] = useState(models);
  const [dragSessionActive, setDragSessionActive] = useState(false);
  // Architecture segmented control (NAM only). Local state — Download All
  // reads this. Initial selection prefers (in order):
  //   1. Explicit `architecture` prop (Select-flow partner-arch).
  //   2. For legacy OAuth partners (partnerArchitectures set but no explicit
  //      arch), the first partner-supported arch the tone actually has models
  //      for — so the segmented control lands on a downloadable segment.
  //   3. The tone's preferred arch (A2 > A1 > Custom).
  const archModelCount = (arch: ArchitectureVersion): number => {
    if (arch === ARCHITECTURE_VERSION.A1) return tone.a1_models_count ?? 0;
    if (arch === ARCHITECTURE_VERSION.A2) return tone.a2_models_count ?? 0;
    return tone.custom_models_count ?? 0;
  };
  const initialArchitecture: ArchitectureVersion =
    architecture ??
    (partnerArchitectures?.find((a) => archModelCount(a) > 0) ?? preferredArchitecture(tone));
  const [selectedArchitecture, setSelectedArchitecture] =
    useState<ArchitectureVersion>(initialArchitecture);
  // Lazy-fetch tracking. The tone page SSRs only the default-arch's models to
  // keep the initial payload small (core = ['2'], Select = partner arch). When
  // the user toggles the segmented control to another arch, we fetch that
  // arch's models from the same `get_tone_with_models` RPC and merge them in.
  // The set tracks which arches have been fetched so we never refetch. Seed
  // from the arches actually present in `models` — `initialArchitecture` can
  // diverge from what SSR fetched (e.g. SSR=['2'], A1-only tone → lands on A1).
  const [fetchedArchitectures, setFetchedArchitectures] = useState<Set<ArchitectureVersion>>(
    () => archesPresentIn(models)
  );
  const [isLoadingArchitecture, setIsLoadingArchitecture] = useState(false);
  // First-A2-download modal: shows once per browser when an A2 model is
  // downloaded (per-row or Download All when A2 is selected). Select handoffs
  // don't bundle files so don't trigger this.
  const [showFirstA2Modal, setShowFirstA2Modal] = useState(false);
  const triggerFirstA2Modal = () => {
    if (isApiSelect) return;
    if (shouldShowFirstA2Modal()) setShowFirstA2Modal(true);
  };
  const pluginDownload = usePluginDownloadIntercept(!isApiSelect);

  // Re-sync with props when the server-rendered list changes. Reset the
  // fetched-arch tracker so a fresh SSR doesn't leave stale "we already have
  // it" entries pointing at the previous tone.
  useEffect(() => {
    setLocalModels(models);
    setFetchedArchitectures(archesPresentIn(models));
  }, [models]);

  // Lazy-fetch on segment toggle. Only NAM tones have multi-arch segments —
  // non-NAM shows everything and never needs to refetch. Short-circuit when
  // the SSR-provided per-arch count is 0: we already know there are no models
  // for that arch from `tones_aggs`, no RPC round-trip needed. We still mark
  // the arch as "fetched" so we don't loop on subsequent toggles.
  useEffect(() => {
    if (platform !== FORMAT.NAM) return;
    if (fetchedArchitectures.has(selectedArchitecture)) return;
    if (archModelCount(selectedArchitecture) === 0) {
      setFetchedArchitectures((prev) => {
        const next = new Set(prev);
        next.add(selectedArchitecture);
        return next;
      });
      return;
    }
    let cancelled = false;
    setIsLoadingArchitecture(true);
    const supabase = createClient();
    getToneByIdRpc(supabase, toneId, [selectedArchitecture]).then((result) => {
      if (cancelled) return;
      const incoming = (result?.models ?? []) as ModelWithChildren[];
      if (incoming.length) {
        setLocalModels((prev) => {
          const seen = new Set(prev.map((m) => m.id));
          const additions = incoming.filter((m) => !seen.has(m.id));
          return additions.length ? [...prev, ...additions] : prev;
        });
      }
      setFetchedArchitectures((prev) => {
        const next = new Set(prev);
        next.add(selectedArchitecture);
        return next;
      });
      setIsLoadingArchitecture(false);
    }).catch(() => {
      if (cancelled) return;
      setIsLoadingArchitecture(false);
    });
    return () => {
      cancelled = true;
    };
    // archModelCount is a stable function of `tone` — listing `tone` in deps
    // would re-run on unrelated tone field changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedArchitecture, platform, toneId, fetchedArchitectures]);

  // Global smooth scroll (see SmoothScrollHandler) fights dnd-kit autoscroll / wheel while dragging.
  useLayoutEffect(() => {
    if (!dragSessionActive) return;
    const prev = document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = 'auto';
    return () => {
      document.documentElement.style.scrollBehavior = prev;
    };
  }, [dragSessionActive]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { delay: 200, tolerance: 5 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: 5 },
    }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const isNam = platform === FORMAT.NAM;
  const filteredModels = useMemo(() => {
    if (!isNam) return localModels;
    return localModels.filter((m) => m.architecture_version === selectedArchitecture);
  }, [isNam, localModels, selectedArchitecture]);
  // Partner-arch gating: when the OAuth partner can't accept the user's
  // currently selected architecture, the model list goes non-interactive and
  // the Download All button is disabled. `null` partnerArchitectures = no
  // gating (non-OAuth Select / core).
  const isCurrentArchSupportedByPartner =
    partnerArchitectures === null || partnerArchitectures.includes(selectedArchitecture);

  const isTraining = filteredModels.some(
    (model) => model.training && !model.training.is_error && !model.training.is_success
  );
  const isError = filteredModels.some((model) => model.training && model.training.is_error);

  const hasMoreThan10 = filteredModels.length > 10;
  const reorderEnabled = canReorder && filteredModels.length > 1;

  const onDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    // Drag operates on the visible (architecture-filtered) list. NAM tones
    // lazy-load models per architecture, so the client never holds the full
    // set — we send only the reordered subset and let the endpoint merge it
    // into the authoritative full ordering. The local merge below keeps the
    // optimistic UI in sync.
    const oldIndex = filteredModels.findIndex((m) => m.id === active.id);
    const newIndex = filteredModels.findIndex((m) => m.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reorderedFiltered = arrayMove(filteredModels, oldIndex, newIndex);
    const filteredIds = new Set(reorderedFiltered.map((m) => m.id));
    let cursor = 0;
    const next = localModels.map((m) =>
      filteredIds.has(m.id) ? reorderedFiltered[cursor++] : m
    );

    const previous = localModels;
    setLocalModels(next);

    try {
      const res = await fetch('/api/models/reorder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          toneId,
          orderedModelIds: reorderedFiltered.map((m) => m.id),
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        console.error('Failed to reorder models:', err);
        setLocalModels(previous);
      } else {
        // No router.refresh(): the optimistic localModels update already
        // reflects the persisted order. Refreshing re-SSRs only the default
        // architecture, which resets localModels and forces a refetch of the
        // viewed arch — a visible "no models" -> "N models" blink.
        analytics.capture('model.reordered', { tone_id: toneId });
      }
    } catch (err) {
      console.error('Failed to reorder models:', err);
      setLocalModels(previous);
    }
  };

  const renderedRows = filteredModels.map((model, index) => {
    const isLast = index === filteredModels.length - 1;
    const hidden = !showAll && index >= 10;
    if (reorderEnabled) {
      return (
        <SortableModel
          key={model.id}
          model={model}
          toneId={toneId}
          userId={userId}
          platform={platform}
          isLast={isLast}
          hidden={hidden}
          isApiSelect={isApiSelect}
          isOAuthFlow={isOAuthFlow}
          dragSessionActive={dragSessionActive}
          onA2Downloaded={triggerFirstA2Modal}
          interceptDownload={pluginDownload.intercept}
        />
      );
    }
    return (
      <div key={model.id} className={`w-full ${hidden ? 'hidden' : ''}`}>
        <div className="w-full min-w-0 px-4 lg:px-8 py-6">
          <Model
            model={model}
            toneId={toneId}
            userId={userId}
            platform={platform}
            isApiSelect={isApiSelect}
            isOAuthFlow={isOAuthFlow}
            onA2Downloaded={triggerFirstA2Modal}
            interceptDownload={pluginDownload.intercept}
          />
        </div>
        {!isLast && (
          <div
            className="mt-6 h-px bg-zinc-700 shrink-0 mx-4 lg:mx-8"
            role="separator"
            aria-hidden
          />
        )}
      </div>
    );
  });

  // While we're lazy-fetching a new arch's models the local filter returns
  // an empty list — show a loading row instead of "No Models" so the UI
  // doesn't flash "no results" for the half-second the RPC takes.
  const showArchitectureLoading = isLoadingArchitecture && filteredModels.length === 0;

  const expandContent = (
    <div className={isCurrentArchSupportedByPartner ? undefined : 'opacity-40 pointer-events-none'}>
    <Expand
      title={t(platform === FORMAT.IR ? 'countIr' : 'count', { count: filteredModels.length })}
      isTraining={isTraining}
      isError={isError}
      defaultExpanded={filteredModels.length > 0 || showArchitectureLoading}
      disabled={filteredModels.length === 0 && !showArchitectureLoading}
    >
      {showArchitectureLoading ? (
        <div className="py-8 flex justify-center">
          <LoadingDots />
        </div>
      ) : reorderEnabled ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={() => setDragSessionActive(true)}
          onDragEnd={(e) => {
            setDragSessionActive(false);
            void onDragEnd(e);
          }}
          onDragCancel={() => setDragSessionActive(false)}
        >
          <SortableContext
            items={filteredModels.map((m) => m.id)}
            strategy={verticalListSortingStrategy}
          >
            {renderedRows}
          </SortableContext>
        </DndContext>
      ) : (
        renderedRows
      )}
      {hasMoreThan10 && !showAll && (
        <div className="py-6 px-4 lg:px-8 flex justify-center">
          <Button isSecondary onClick={() => setShowAll(true)} className="px-6 py-2">
            View All
          </Button>
        </div>
      )}
    </Expand>
    </div>
  );

  // IR (and non-NAM in general): no segmented control, no internal Download
  // All — the page file renders its own.
  if (!isNam) {
    return (
      <>
        {expandContent}
        {pluginDownload.modal}
      </>
    );
  }

  // NAM: segmented control on the left, Download All on the right (both core
  // and Select). Select uses the partner-arch as the initial selection and
  // disables the Download All when the user picks an architecture the partner
  // can't accept.
  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-row gap-4 items-center justify-between flex-wrap">
        <ArchitectureSegmentedControl
          value={selectedArchitecture}
          onChange={setSelectedArchitecture}
        />
        {isApiSelect ? (
          <DownloadAllButton
            tone={tone}
            userId={userId}
            models={localModels}
            isApiSelect
            isOAuthFlow={isOAuthFlow}
            isGearUnsupported={isGearUnsupported}
            selectGears={selectGears}
            forceDisabled={!isCurrentArchSupportedByPartner || filteredModels.length === 0}
          />
        ) : (
          <DownloadAllButton
            tone={tone}
            userId={userId}
            models={localModels}
            architectureFilter={selectedArchitecture}
            onDownloadStart={
              selectedArchitecture === ARCHITECTURE_VERSION.A2
                ? triggerFirstA2Modal
                : undefined
            }
          />
        )}
      </div>
      {expandContent}
      <FirstA2DownloadModal
        open={showFirstA2Modal}
        onClose={() => setShowFirstA2Modal(false)}
      />
      {pluginDownload.modal}
    </div>
  );
};

export default Models;
